// The `plan-files` headline-skip fixture (#5189) — a sibling module for the
// same 300-line-ceiling reason `plan-story-custom-mocks.ts` and
// `plan-story-bulk-mocks.ts` are: `plan-stories-mocks.ts` sits at its cap.
//
// #5172 records a `plan-files` bookkeeping group at position 0 of every
// STORED plan (real id: `PLAN_FILES_GROUP_ID`). `summarise()` (`plans-mocks.ts`)
// has to skip it when picking a plan's headline, exactly like the real
// `selectPlanHeadlines` (`@developerz/db`) does — and both stories below are
// what pins that in the mock plane, the same way
// `listing.integration.test.ts` pins it against real Postgres: one plan whose
// real first slice sits BEHIND the bookkeeping group, and one plan where that
// group is the ONLY group (nothing has been approved past it yet), so the
// headline has nowhere else to fall but back onto it.
//
// Same `group()` idiom as `plan-story-custom-mocks.ts` / `plan-story-bulk-mocks.ts`
// (`disposition`/`stallReason` DERIVED by `planGroupDisposition`, never typed
// by hand — see either sibling's header for why a hand-typed verdict drifted
// on PR #3780).

import {
  PLAN_FILES_GROUP_ID,
  type PlanProjectionPullRequest,
  type PlanProjectionTask,
  planGroupDisposition,
} from '@developerz/domain';
import { at } from './mock-session';
import type { StoryGroup } from './plan-leg-mocks';
import type { PlanStory } from './plan-stories-mocks';

// TWO repos, deliberately: the plan-files group's own repo must LOSE to the
// work slice's repo in the headline, never merely tie with it.
const DOCS_REPO = 'developerz-ai/plan-documents';
const WORK_REPO = 'developerz-ai/rate-limiter';

function group(input: {
  groupId: string;
  position: number;
  title: string;
  repoFullName: string;
  status: StoryGroup['status'];
  dependsOn: readonly string[];
  task: PlanProjectionTask | null;
  pullRequest: PlanProjectionPullRequest | null;
}): StoryGroup {
  const verdict = planGroupDisposition({
    groupStatus: input.status,
    task:
      input.task === null
        ? null
        : {
            status: input.task.status,
            pullRequestNumber: input.task.pullRequestNumber,
            outcomeKind: 'pr',
            noWorkSettled: false,
            opsActionRefused: false,
          },
    pullRequestState: input.pullRequest?.state ?? null,
  });
  const { dependsOn, ...rest } = input;
  return { ...rest, dependsOn: [...dependsOn], ...verdict, noWork: null, heldOn: null };
}

/**
 * The platform's own "commit the documents" group — always position 0, no
 * task (`plan-groups/plan-files.ts`'s docblock: "mints NO task for it"),
 * merged through its own anchored pull request.
 */
function planFilesGroup(title: string): StoryGroup {
  return group({
    groupId: PLAN_FILES_GROUP_ID,
    position: 0,
    title,
    repoFullName: DOCS_REPO,
    status: 'merged',
    dependsOn: [],
    task: null,
    pullRequest: null,
  });
}

/** The plan's real first slice — dispatched, in flight, no task done yet. */
function workGroup(): StoryGroup {
  return group({
    groupId: 'g_work',
    position: 1,
    title: 'Add the --since range filter',
    repoFullName: WORK_REPO,
    status: 'dispatched',
    dependsOn: [PLAN_FILES_GROUP_ID],
    task: { id: 'tsk_pf_work', status: 'in_progress', pullRequestNumber: null },
    pullRequest: null,
  });
}

/**
 * `plan_mock_planfiles_lead` — a plan-files group at position 0, a real work
 * slice at position 1. The index row must name the WORK slice's title and
 * repo, never the boilerplate "Commit the documents of plan …" one.
 */
export const PLANFILES_LEAD_STORY: PlanStory = {
  planId: 'plan_mock_planfiles_lead',
  status: 'open',
  rawTaskId: null,
  initiatedBy: 'system',
  owner: null,
  createdIso: at('2026-09-20'),
  updatedIso: at('2026-09-27'),
  groups: [
    planFilesGroup('Commit the documents of plan docs/plans/2026/09/20/189-rate-limit'),
    workGroup(),
  ],
};

/**
 * `plan_mock_planfiles_only` — the plan-files group is the plan's ONLY group
 * (nothing has been approved past it yet). The headline has to fall back to
 * ITS title, not read as a plan with no headline at all.
 */
export const PLANFILES_ONLY_STORY: PlanStory = {
  planId: 'plan_mock_planfiles_only',
  status: 'open',
  rawTaskId: null,
  initiatedBy: 'system',
  owner: null,
  createdIso: at('2026-09-26'),
  updatedIso: at('2026-09-26'),
  groups: [planFilesGroup('Commit the documents of plan docs/plans/2026/09/26/190-audit-log')],
};
